import React from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Navigation } from '../../components/layout/Navigation';
import { Footer } from '../../components/layout/Footer';
import { Container } from '../../components/ui/Container';
import { Button } from '../../components/ui/Button';
import { Spinner } from '../../components/ui/Spinner';
import { useCurrentUser } from '../../contexts/AuthContext';
import { markCoursePurchased } from '../../services/billing/local-billing';
import { paymentsApi } from '../../services/api/payments';

type VerificationState = Readonly<{
  loading: boolean;
  verified: boolean;
  status: 'paid' | 'unpaid' | 'pending' | 'invalid';
  accessGranted: boolean;
  accessCode: string | null;
  courseId: string | null;
  reason: string | null;
}>;

const initialState: VerificationState = {
  loading: true,
  verified: false,
  status: 'invalid',
  accessGranted: false,
  accessCode: null,
  courseId: null,
  reason: null,
};

export const PurchaseSuccessPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const currentUser = useCurrentUser();
  const [retryToken, setRetryToken] = React.useState(0);
  const [state, setState] = React.useState<VerificationState>(initialState);

  const sessionId = String(searchParams.get('session_id') || '').trim();
  const requestedCourseId = String(searchParams.get('course_id') || '').trim().toLowerCase();

  React.useEffect(() => {
    let cancelled = false;

    const verify = async () => {
      if (!sessionId) {
        setState({
          loading: false,
          verified: false,
          status: 'invalid',
          accessGranted: false,
          accessCode: null,
          courseId: null,
          reason: 'Missing Stripe session id in success URL.',
        });
        return;
      }

      setState((prev) => ({ ...prev, loading: true }));

      const result = await paymentsApi.verifySession(sessionId, {
        courseId: requestedCourseId || undefined,
        userId: currentUser?.id,
        email: currentUser?.email,
      });

      if (cancelled) return;

      if (!result.success) {
        setState({
          loading: false,
          verified: false,
          status: 'invalid',
          accessGranted: false,
          accessCode: null,
          courseId: requestedCourseId || null,
          reason: result.error.message,
        });
        return;
      }

      const verifiedCourseId = (result.data.course_id || requestedCourseId || '').trim().toLowerCase() || null;
      const hasPaidCourseAccess =
        result.data.verified && result.data.status === 'paid' && result.data.access_granted;

      if (hasPaidCourseAccess && verifiedCourseId) {
        markCoursePurchased(verifiedCourseId, sessionId);
      }

      setState({
        loading: false,
        verified: result.data.verified,
        status: result.data.status,
        accessGranted: result.data.access_granted,
        accessCode: result.data.access_code || null,
        courseId: verifiedCourseId,
        reason: result.data.reason || null,
      });
    };

    verify();

    return () => {
      cancelled = true;
    };
  }, [sessionId, requestedCourseId, currentUser?.id, currentUser?.email, retryToken]);

  const copyAccessCode = async () => {
    if (!state.accessCode) return;
    try {
      await navigator.clipboard.writeText(state.accessCode);
      toast.success('Access code copied');
    } catch {
      toast.error('Failed to copy access code');
    }
  };

  return (
    <div className="min-h-screen bg-brand-dark">
      <Navigation />

      <main className="pt-24 pb-12">
        <Container className="max-w-3xl">
          <div className="bg-brand-gray-800 rounded-xl p-8 border border-brand-gray-700">
            <h1 className="text-3xl font-bold text-white mb-4">Purchase Verification</h1>

            {state.loading ? (
              <div className="flex items-center gap-3 text-gray-300 py-6">
                <Spinner size="md" />
                <span>Verifying your Stripe payment...</span>
              </div>
            ) : (
              <>
                {state.status === 'paid' && state.accessGranted && !state.accessCode && (
                  <div className="text-green-300 mb-6">
                    Payment verified and course access granted.
                  </div>
                )}

                {state.status === 'paid' && state.accessGranted && state.accessCode && (
                  <div className="mb-6">
                    <p className="text-green-300 mb-3">
                      Payment verified and course access granted. This code is optional:
                    </p>
                    <div className="bg-brand-dark border border-brand-gold/40 rounded-lg p-4 flex items-center justify-between gap-3">
                      <code className="text-brand-gold text-lg font-semibold break-all">{state.accessCode}</code>
                      <Button size="sm" variant="outline" onClick={copyAccessCode}>
                        Copy
                      </Button>
                    </div>
                  </div>
                )}

                {state.status === 'pending' && (
                  <p className="text-yellow-300 mb-6">
                    Your payment is still pending. This can happen with async payment methods. Retry verification shortly.
                  </p>
                )}

                {(state.status === 'unpaid' || state.status === 'invalid' || !state.accessGranted) && (
                  <p className="text-red-300 mb-6">
                    {state.reason || 'Payment could not be verified for course access.'}
                  </p>
                )}

                <div className="flex flex-wrap gap-3">
                  {state.courseId && state.status === 'paid' && state.accessGranted && (
                    <Button
                      variant="gold"
                      onClick={() => navigate(`/my-courses/${encodeURIComponent(state.courseId!)}`)}
                    >
                      Open Course
                    </Button>
                  )}

                  {state.status === 'pending' && (
                    <Button variant="gold" onClick={() => setRetryToken((value) => value + 1)}>
                      Retry Verification
                    </Button>
                  )}

                  <Button variant="outline" onClick={() => navigate('/courses')}>
                    Browse Courses
                  </Button>
                </div>
              </>
            )}
          </div>
        </Container>
      </main>

      <Footer />
    </div>
  );
};
